import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import ctBrainImg from "../assests/ct_brain.png";
import ctBrain2Img from "../assests/ct_brain2.png";
import ctChestImg from "../assests/ct_chest.webp";
import chestSoftImg from "../assests/chest_soft.jpg";
import ctKubImg from "../assests/ct_kub.webp";
import ctAbdominImg from "../assests/ct_abdomin.png";
import mriBrainImg from "../assests/mri_brain.webp";
import mriAngiogramImg from "../assests/mri_angiogram.webp";
import mriSpineImg from "../assests/mri_spine.jpg";
import mriAbdominalImg from "../assests/mri_abdominal.png";
import mriKneeImg from "../assests/mri_knee.png";
import mriAnkleImg from "../assests/mri_ankle.jpeg";

/* ── Data ─────────────────────────────────────────────────────────────────── */
const facilities = [
  {
    text: "1.5 Tesla Hdxt MRI",
    highlight: true,
  },
  { text: "GE Brivo 32 CT Scan", highlight: true },
  { text: "High Powered, High Resolution Magnet" },
  { text: "Fast Scan Time" },
  { text: "Latest Generation MRI Scan" },
  { text: "Whole Body Imaging" },
  {
    text: "Brain – Acute Stroke Detection, Routine & Stroke Protocol, Epilepsy (Seizure) Protocol, Angiogram – Venography",
  },
  { text: "Spine" },
  { text: "Joint, Extremities & Digits" },
  { text: "Pelvic Organs (Uterus & Ovaries)" },
  { text: "MRCP (MR-Pancreato-Cholagiography)" },
  { text: "Abdomen & Chest" },
  { text: "Angiograms – Carotid & Cranial, Renal & Peripheral" },
  { text: "MR Fistulogram (Anal)" },
  { text: "OBS Imaging (Pregnancy)" },
  { text: "MR Urography" },
];

const ctScans = [
  {
    url: ctBrainImg,
    label: "Brain CT – Axial",
  },
  {
    url: ctBrain2Img,
    label: "Spine CT – Sagittal",
  },
  {
    url: ctChestImg,
    label: "Chest CT",
  },
  {
    url: chestSoftImg,
    label: "Abdomen CT",
  },
  {
    url: ctKubImg,
    label: "CT Angio",
  },
  {
    url: ctAbdominImg,
    label: "Abdomen CT 2",
  },
];

const mriScans = [
  {
    url: mriBrainImg,
    label: "Brain MRI – T2",
  },
  {
    url: mriAngiogramImg,
    label: "MR Angiography",
  },
  {
    url: mriSpineImg,
    label: "Spine MRI",
  },
  {
    url: mriAbdominalImg,
    label: "Knee MRI",
  },
  {
    url: mriKneeImg,
    label: "MRCP",
  },
  {
    url: mriAnkleImg,
    label: "Ankle MRI",
  },
];

/* ── Scan image column ─────────────────────────────────────────────────────── */
function ScanColumn({ label, color, scans, direction }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <div ref={ref} className="flex flex-col items-center">
      {/* Column label */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mb-3 px-5 py-1.5 rounded-full font-poppins font-black text-xs tracking-[0.2em] uppercase text-white border"
        style={{ background: `${color}30`, borderColor: `${color}60` }}
      >
        {label}
      </motion.div>

      {/* Images */}
      <div className="flex flex-col gap-2.5">
        {scans.map((s, i) => (
          <motion.div
            key={i}
            initial={{
              opacity: 0,
              x: direction === "left" ? -50 : 50,
              scale: 0.9,
            }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{
              delay: 0.1 + i * 0.1,
              duration: 0.55,
              ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{ scale: 1.06, y: -4 }}
            className="relative rounded-xl overflow-hidden shadow-2xl group cursor-pointer"
            style={{
              width: 190,
              height: 145,
              border: `1px solid ${color}40`,
              boxShadow: `0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px ${color}20`,
              background: "#000",
            }}
          >
            <img
              src={s.url}
              alt={s.label}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
            <div className="absolute top-1.5 right-1.5 w-3 h-3 border-t border-r border-white/40" />
            <div className="absolute bottom-1.5 right-1.5 w-3 h-3 border-b border-r border-white/40" />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ── Main section ──────────────────────────────────────────────────────────── */
export default function FacilitiesSection() {
  const listRef = useRef(null);
  const listInView = useInView(listRef, { once: true, margin: "-80px" });

  return (
    <section
      id="facilities"
      className="py-24 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, #080f2e 0%, #0f1e5a 40%, #1a1050 100%)",
      }}
    >
      {/* Animated background orbs */}
      <motion.div
        animate={{ scale: [1, 1.3, 1], opacity: [0.08, 0.16, 0.08] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, #1d4ed855, transparent 70%)",
        }}
      />
      <motion.div
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.06, 0.14, 0.06] }}
        transition={{
          duration: 14,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 4,
        }}
        className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, #7c3aed44, transparent 70%)",
        }}
      />

      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Title Banner ── */}
        <motion.div
          initial={{ opacity: 0, y: -30, scale: 0.92 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center mb-14"
        >
          <div className="relative">
            <div
              className="absolute inset-0 blur-2xl opacity-50 rounded-xl"
              style={{
                background: "linear-gradient(135deg, #38bdf8, #0ea5e9)",
              }}
            />
            <div className="relative flex items-stretch rounded-xl overflow-hidden shadow-2xl">
              <div className="w-3 bg-blue-700 shrink-0" />
              <div
                className="px-14 py-4"
                style={{
                  background:
                    "linear-gradient(135deg, #7dd3fc 0%, #38bdf8 50%, #0ea5e9 100%)",
                }}
              >
                <h2
                  className="font-poppins font-black text-white text-3xl sm:text-4xl tracking-wide whitespace-nowrap"
                  style={{ textShadow: "0 1px 4px rgba(0,0,0,0.2)" }}
                >
                  Facilities Available
                </h2>
              </div>
              <div className="w-3 bg-blue-700 shrink-0" />
            </div>
          </div>
        </motion.div>

        {/* ── Three-column body ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr_180px] gap-6 xl:gap-10 items-start">
          {/* Left — CT */}
          <div className="hidden lg:flex justify-center">
            <ScanColumn
              label="CT SCAN"
              color="#0ea5e9"
              scans={ctScans}
              direction="left"
            />
          </div>

          {/* Centre — Facility list */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl overflow-hidden"
            style={{
              boxShadow:
                "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)",
            }}
          >
            {/* Card header bar */}
            <div
              className="px-6 py-3.5 flex items-center gap-3"
              style={{ background: "linear-gradient(90deg, #1d4ed8, #7c3aed)" }}
            >
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
              </div>
              <span className="text-white/75 text-[11px] font-mono tracking-[0.15em] uppercase ml-2">
                Indur CT &amp; MRI — Services
              </span>
            </div>

            {/* List */}
            <ul ref={listRef} className="bg-white divide-y divide-gray-100">
              {facilities.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={listInView ? { opacity: 1, x: 0 } : {}}
                  transition={{
                    delay: 0.04 + i * 0.05,
                    duration: 0.4,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={`group flex items-start gap-3 px-6 py-3 transition-colors duration-200 cursor-default ${
                    item.highlight
                      ? "bg-amber-50 hover:bg-amber-100/80"
                      : "hover:bg-blue-50/60"
                  }`}
                >
                  {/* Pulsing bullet */}
                  <span className="relative mt-[5px] shrink-0">
                    <span
                      className="absolute inset-0 rounded-full animate-ping opacity-40"
                      style={{
                        background: item.highlight ? "#d97706" : "#e8523a",
                      }}
                    />
                    <span
                      className="relative block w-2.5 h-2.5 rounded-full"
                      style={{
                        background: item.highlight ? "#d97706" : "#e8523a",
                      }}
                    />
                  </span>
                  <span
                    className={`text-sm leading-relaxed flex-1 ${
                      item.highlight
                        ? "font-bold text-gray-900"
                        : "font-medium text-gray-700 group-hover:text-gray-900"
                    }`}
                  >
                    {item.text}
                  </span>
                  {item.highlight && (
                    <span className="shrink-0 mt-0.5 self-start text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                      ✓ Available
                    </span>
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right — MRI */}
          <div className="hidden lg:flex justify-center">
            <ScanColumn
              label="MRI SCAN"
              color="#7c3aed"
              scans={mriScans}
              direction="right"
            />
          </div>
        </div>

        {/* Mobile scan images */}
        <div className="lg:hidden mt-8 grid grid-cols-2 gap-4">
          {[
            { label: "CT SCAN", color: "#0ea5e9", scans: ctScans.slice(0, 3) },
            {
              label: "MRI SCAN",
              color: "#7c3aed",
              scans: mriScans.slice(0, 3),
            },
          ].map(({ label, color, scans }) => (
            <div key={label}>
              <p
                className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 px-3 py-0.5 rounded-full inline-block"
                style={{
                  color,
                  background: `${color}20`,
                  border: `1px solid ${color}40`,
                }}
              >
                {label}
              </p>
              <div className="flex flex-col gap-2">
                {scans.map((s, i) => (
                  <div
                    key={i}
                    className="relative rounded-lg overflow-hidden"
                    style={{ height: 68, border: `1px solid ${color}30` }}
                  >
                    <img
                      src={s.url}
                      alt={s.label}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <p className="absolute bottom-1 left-2 text-white text-[9px] font-semibold">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
